import React, { useRef, useState, useEffect } from "react";
import Tooltip from "@mui/material/Tooltip";

interface TruncatedTextWithTooltipProps {
  text: string;
  style?: any;
  className?: string;
  limit?: number;
}

export const TruncatedTextWithTooltip: React.FC<TruncatedTextWithTooltipProps> = ({
  text,
  style,
  className,
  limit,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  // Limit Logic
  const hasManualLimit = typeof limit === "number";
  const isManuallyTruncated = hasManualLimit && text?.length > limit;
  
  // Display Logic
  const displayText = isManuallyTruncated 
    ? `${text.slice(0, limit)}...` 
    : (text === "NON_STEM" || text === "NONSTEM" ? "NON-STEM" : text);

  const checkOverflow = () => {
    if (textRef.current && !hasManualLimit) {
      setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  };

  useEffect(() => {
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [text, limit]);

  // Determine if tooltip should be active
  const shouldShowTooltip = hasManualLimit ? isManuallyTruncated : isOverflowing;

  return (
    <Tooltip
      title={text}
      disableHoverListener={!shouldShowTooltip}
      disableTouchListener={!shouldShowTooltip}
      open={shouldShowTooltip ? open : false}
      onClose={() => setOpen(false)}
      placement="top"
      enterTouchDelay={50}
    >
      <span
        ref={textRef}
        onClick={(e) => {
          if (shouldShowTooltip) {
            e.stopPropagation();
            setOpen((prev) => !prev);
          }
        }}
        className={className}
        style={{
          display: "block",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: hasManualLimit ? "clip" : "ellipsis",
          maxWidth: "100%",
          cursor: shouldShowTooltip ? "pointer" : "default",
          ...style,
        }}
      >
        {displayText}
      </span>
    </Tooltip>
  );
};
